import { useState, useCallback, useEffect } from 'react';
import { notify } from '../utils/toast';

export const useSpeech = () => {
  const [speaking, setSpeaking] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState<SpeechSynthesis | null>(null);
  const [utterance, setUtterance] = useState<SpeechSynthesisUtterance | null>(null);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  // Initialize speech synthesis
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      setSpeechSynthesis(window.speechSynthesis);
      
      const loadVoices = () => {
        const availableVoices = window.speechSynthesis.getVoices();
        if (availableVoices.length > 0) {
          setVoices(availableVoices);
        }
      };

      loadVoices(); // Try loading immediately
      
      // Handle dynamic voice loading
      window.speechSynthesis.onvoiceschanged = loadVoices;

      return () => {
        window.speechSynthesis.onvoiceschanged = null;
      };
    }
  }, []);

  // Keep speech synthesis alive and handle visibility changes
  useEffect(() => {
    if (!speechSynthesis) return;

    const handleVisibilityChange = () => {
      if (document.hidden && speaking) {
        speechSynthesis.pause();
      } else if (!document.hidden && speaking) {
        speechSynthesis.resume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [speechSynthesis, speaking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (speaking) {
        window.speechSynthesis?.cancel();
      }
    };
  }, [speaking]);

  const stopSpeaking = useCallback(() => {
    if (speechSynthesis) {
      speechSynthesis.cancel();
      setSpeaking(false);
      setUtterance(null);
    }
  }, [speechSynthesis]);

  const getBestVoice = useCallback((text: string) => {
    // Simple language detection - can be enhanced for more accuracy
    const isEnglishText = /^[a-zA-Z0-9\s.,!?'"-]+$/.test(text);
    const langCode = isEnglishText ? 'en' : 'en-US';

    // Try to find a high-quality voice
    const bestVoice = voices.find(voice => {
      const isEnglish = voice.lang.startsWith('en');
      const isHighQuality = 
        voice.name.includes('Premium') ||
        voice.name.includes('Enhanced') ||
        (voice.name.includes('Google') && isEnglish);
      
      return isHighQuality;
    });

    // Fallback to any English voice
    if (!bestVoice) {
      return voices.find(voice => voice.lang.startsWith('en')) || voices[0];
    }

    return bestVoice;
  }, [voices]);

  const speak = useCallback((text: string) => {
    if (!speechSynthesis) {
      notify.error('Text-to-speech is not supported in your browser');
      return;
    }

    // Stop any ongoing speech
    stopSpeaking();

    try {
      // Split text into manageable chunks at sentence boundaries
      const chunks = text.match(/[^.!?]+[.!?]+/g) || [text];
      let currentChunk = 0;

      const speakNextChunk = () => {
        if (currentChunk < chunks.length) {
          const chunk = chunks[currentChunk].trim();
          const newUtterance = new SpeechSynthesisUtterance(chunk);
          
          // Select the best voice for this text
          const bestVoice = getBestVoice(chunk);
          if (bestVoice) {
            newUtterance.voice = bestVoice;
            newUtterance.lang = bestVoice.lang; // Ensure language matches voice
          }

          // Configure voice settings
          newUtterance.rate = 1.0;  // Normal speed
          newUtterance.pitch = 1.0; // Normal pitch
          newUtterance.volume = 1.0; // Full volume

          // Handle chunk completion
          newUtterance.onend = () => {
            currentChunk++;
            if (currentChunk < chunks.length) {
              setTimeout(speakNextChunk, 250); // Add small pause between sentences
            } else {
              setSpeaking(false);
              setUtterance(null);
            }
          };

          // Handle errors
          newUtterance.onerror = (event) => {
            console.error('Speech synthesis error:', event);
            
            // Show warning for interruption, error for other issues
            if (event.error === 'interrupted') {
              notify.info('Read aloud stopped by user');
            } else {
              notify.error('Failed to read text aloud');
            }
            
            setSpeaking(false);
            setUtterance(null);
          };

          setUtterance(newUtterance);
          setSpeaking(true);
          speechSynthesis.speak(newUtterance);
        }
      };

      speakNextChunk();
    } catch (error) {
      console.error('Speech synthesis error:', error);
      notify.error('Failed to read text aloud');
      setSpeaking(false);
      setUtterance(null);
    }
  }, [speechSynthesis, stopSpeaking, getBestVoice]);

  return {
    speak,
    stopSpeaking,
    speaking,
    supported: !!speechSynthesis
  };
};